import { fetchWithAuth, getCSRFToken } from '../components/auth';

const AZURE_STORAGE_ORIGIN = 'https://fiberconstoragedev01.blob.core.windows.net';
const API_HOST = process.env.REACT_APP_ICC_TOOL_API_HOST;

function resolveUploadUrl(sasUrl) {
    if (process.env.NODE_ENV === 'development') {
        return sasUrl.replace(AZURE_STORAGE_ORIGIN, '/azure-upload-proxy');
    }
    return sasUrl;
}

/**
 * Fetches a write SAS URL for a blob from the generic backend endpoint.
 *
 * GET /blob/upload-url/?filename=<filename>&prefix=<prefix>
 *
 * @param {string} filename - File name (e.g. 'clip.mp4')
 * @param {string} prefix   - Blob path prefix (e.g. 'videos/incoming/')
 * @returns {Promise<{ sasUrl: string, blobName: string }>}
 */
export async function getSasUploadUrl(filename, prefix) {
    const url = `${API_HOST}blob/upload-url/?filename=${encodeURIComponent(filename)}&prefix=${encodeURIComponent(prefix)}`;
    const resp = await fetchWithAuth(url, { credentials: 'include' });
    if (!resp.ok) throw new Error('Failed to get upload URL');
    return resp.json();
}

/**
 * Fetches a short-lived read SAS URL for a blob from the generic backend endpoint.
 *
 * GET /blob/read-url/?blobName=<blobName>
 *
 * @param {string} blobName - Full blob path returned by getSasUploadUrl (e.g. 'videos/incoming/clip_<uuid>.mp4')
 * @returns {Promise<{ sasUrl: string }>}
 */
export async function getBlobReadUrl(blobName) {
    const url = `${API_HOST}blob/read-url/?blobName=${encodeURIComponent(blobName)}`;
    const resp = await fetchWithAuth(url, { credentials: 'include' });
    if (!resp.ok) throw new Error('Failed to get read URL');
    return resp.json();
}

/**
 * Fetches a short-lived download SAS URL (Content-Disposition: attachment) for a blob.
 *
 * GET /blob/read-url/?blobName=<blobName>&download=true
 *
 * @param {string} blobName - Full blob path
 * @returns {Promise<{ sasUrl: string }>}
 */
export async function getBlobDownloadUrl(blobName) {
    const url = `${API_HOST}blob/read-url/?blobName=${encodeURIComponent(blobName)}&download=true`;
    const resp = await fetchWithAuth(url, { credentials: 'include' });
    if (!resp.ok) throw new Error('Failed to get download URL');
    return resp.json();
}

/**
 * Deletes a blob that was uploaded but never registered (cleanup on failure).
 *
 * DELETE /blob/temp/?blobName=<blobName>
 *
 * @param {string} blobName - Full blob path returned by getSasUploadUrl
 * @returns {Promise<void>}
 */
export async function deleteTempBlob(blobName) {
    const url = `${API_HOST}blob/temp/?blobName=${encodeURIComponent(blobName)}`;
    const resp = await fetchWithAuth(url, {
        method: 'DELETE',
        headers: { 'X-CSRFToken': getCSRFToken() },
        credentials: 'include'
    });
    if (!resp.ok) throw new Error('Failed to delete temp blob');
}

/**
 * Extracts the first frame of a video file locally via the Canvas API.
 * Resolves with a WebP Blob (max 1280 px wide, 85 % quality), or undefined if
 * the browser does not support the codec.
 * Never throws — a thumbnail failure must not block the main upload.
 * @param {File} file
 * @returns {Promise<Blob|undefined>}
 */
export function extractVideoThumbnail(file) {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        const objectUrl = URL.createObjectURL(file);
        let resolved = false;

        const done = (value) => {
            if (resolved) return;
            resolved = true;
            clearTimeout(timer);
            URL.revokeObjectURL(objectUrl);
            resolve(value);
        };

        // Safety net: if seeked never fires (unsupported codec, stalled load, etc.)
        // resolve undefined so the upload continues without a thumbnail.
        const timer = setTimeout(() => done(undefined), 5000);

        video.addEventListener('seeked', () => {
            if (resolved) return;
            resolved = true;
            clearTimeout(timer);

            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 1280;
            let width = video.videoWidth || 1280;
            let height = video.videoHeight || 720;
            if (width > MAX_WIDTH) {
                height = Math.floor((height * MAX_WIDTH) / width);
                width = MAX_WIDTH;
            }
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(video, 0, 0, width, height);

            URL.revokeObjectURL(objectUrl);
            canvas.toBlob(
                (blob) => resolve(blob || undefined),
                'image/webp',
                0.85
            );
        }, { once: true });
        video.addEventListener('error', () => done(undefined));

        video.src = objectUrl;
        video.currentTime = 0.001;
    });
}

/**
 * Sends the file directly to Azure using the SAS URL as the credential.
 * Does NOT use fetchWithAuth — Azure rejects extra Authorization headers.
 * Uses XMLHttpRequest to expose upload progress events.
 *
 * In development (npm start), requires a proxy in src/setupProxy.js:
 *   /azure-upload-proxy → https://fiberconstoragedev01.blob.core.windows.net
 *
 * @param {string} sasUrl
 * @param {File|Blob} data
 * @param {function(number): void} [onProgress] - callback with percentage 0-100
 * @returns {Promise<void>}
 */
export function uploadBlobToAzure(sasUrl, data, onProgress) {
    const url = resolveUploadUrl(sasUrl);
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        if (onProgress) {
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    // Cap progress at 95% since request is still processing on Azure after the upload completes.
                    onProgress(Math.round((event.loaded / event.total) * 95));
                }
            };
        }
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                if (onProgress) {
                    onProgress(100);
                }
                resolve();
            } else {
                reject(new Error(`Azure upload failed with status ${xhr.status}`));
            }
        };
        xhr.onerror = () => reject(new Error('Azure upload network error'));
        xhr.open('PUT', url);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.send(data);
    });
}
